import { useEffect, useState } from "react";
import {
  Archive,
  ArrowLeft,
  Download,
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  FolderInput,
  Forward,
  Reply,
  ReplyAll,
  Trash2,
} from "lucide-react";
import type { EmailAddress, Mailbox, MessageFull } from "../../lib/api-client";
import { api, ApiError } from "../../lib/api-client";
import { mailboxLabel } from "../../lib/mailbox-labels";
import { htmlToPlainText, sanitizeHeaderText } from "../../lib/sanitize";
import EmailBody from "../../components/EmailBody";
import type { Theme } from "../../lib/theme";
import type { ComposeDraft } from "./ComposePane";

interface MessageViewProps {
  mailboxId: string;
  messageId?: string;
  theme: Theme;
  isTrash: boolean;
  mailboxes: Mailbox[] | null;
  myEmail?: string;
  onSeen: (mailboxId: string, messageId: string) => void;
  onRemoved: (mailboxId: string) => void;
  onCompose: (draft: ComposeDraft) => void;
  // Solo en mobile (ver MailLayout): la lista y la lectura ocupan la
  // pantalla completa una por vez, así que hace falta una forma explícita
  // de volver a la lista — en desktop ambas están siempre visibles lado a
  // lado y no se pasa este prop.
  onBack?: () => void;
}

export default function MessageView({
  mailboxId,
  messageId,
  theme,
  isTrash,
  mailboxes,
  myEmail,
  onSeen,
  onRemoved,
  onCompose,
  onBack,
}: MessageViewProps) {
  const [message, setMessage] = useState<MessageFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);

  useEffect(() => {
    setMessage(null);
    setError(null);
    setActionError(null);
    setIsMoveOpen(false);
    if (!messageId) return;
    api
      .message(messageId)
      .then((msg) => {
        setMessage(msg);
        // Bug reportado: abrir un correo no lo marcaba como leído. Solo
        // dispara el PATCH si de verdad hacía falta (evita un round-trip
        // en cada vez que se re-visita un correo ya leído).
        if (!msg.keywords?.["$seen"]) {
          api
            .markSeen(msg.id, true)
            .then(() => onSeen(mailboxId, msg.id))
            .catch(() => {
              // Best-effort: si falla, el correo queda como no leído en el
              // servidor y el usuario puede reintentar reabriéndolo — no
              // vale la pena bloquear la lectura por esto.
            });
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  async function handleArchive() {
    if (!messageId || isActing) return;
    setIsActing(true);
    setActionError(null);
    try {
      await api.archiveMessage(messageId);
      onRemoved(mailboxId);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? "No se pudo archivar el correo." : "No se pudo archivar el correo.",
      );
    } finally {
      setIsActing(false);
    }
  }

  async function handleMove(targetMailboxId: string) {
    if (!messageId || isActing) return;
    setIsMoveOpen(false);
    setIsActing(true);
    setActionError(null);
    try {
      await api.moveMessage(messageId, targetMailboxId);
      onRemoved(mailboxId);
    } catch (err) {
      setActionError(err instanceof ApiError ? "No se pudo mover el correo." : "No se pudo mover el correo.");
    } finally {
      setIsActing(false);
    }
  }

  async function handleDelete() {
    if (!messageId || isActing) return;
    if (isTrash && !window.confirm("Este correo se eliminará permanentemente. ¿Continuar?")) {
      return;
    }
    setIsActing(true);
    setActionError(null);
    try {
      await api.deleteMessage(messageId);
      onRemoved(mailboxId);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? "No se pudo eliminar el correo." : "No se pudo eliminar el correo.",
      );
    } finally {
      setIsActing(false);
    }
  }

  if (!messageId) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-muted)",
          fontSize: 13,
        }}
      >
        Elegí un correo para leerlo
      </div>
    );
  }

  if (error) {
    return <p style={{ padding: 24, fontSize: 13, color: "#b91c1c" }}>{error}</p>;
  }

  if (!message) {
    return <p style={{ padding: 24, fontSize: 13, color: "var(--text-secondary)" }}>Cargando…</p>;
  }

  // Headers de correo: SIEMPRE texto no confiable, nunca se interpretan
  // como HTML (CLAUDE.md §3.4) — todo lo de acá viaja por JSX (escapa solo)
  // o por sanitizeHeaderText.
  const subject = sanitizeHeaderText(message.subject || "(sin asunto)");
  const fromName = sanitizeHeaderText(message.from?.[0]?.name || message.from?.[0]?.email);
  const fromEmail = sanitizeHeaderText(message.from?.[0]?.email);

  const htmlPartId = message.htmlBody[0]?.partId;
  const textPartId = message.textBody[0]?.partId;
  const html = htmlPartId ? message.bodyValues[htmlPartId]?.value : undefined;
  const text = !html && textPartId ? message.bodyValues[textPartId]?.value : undefined;

  const moveTargets = mailboxes?.filter((mb) => mb.id !== mailboxId) ?? [];

  function handleReplyAction(mode: "reply" | "replyAll" | "forward") {
    if (!message) return;
    const plainBody = html ? htmlToPlainText(html) : text ?? "";
    onCompose(buildReplyDraft(message, mode, myEmail, plainBody));
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflowY: "auto",
        padding: "20px 24px",
      }}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            border: "none",
            background: "transparent",
            color: "var(--text-secondary)",
            fontSize: 13,
            padding: "4px 0",
            marginBottom: 12,
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={16} /> Volver
        </button>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => handleReplyAction("reply")}
          title="Responder"
          style={toolbarButtonStyle(false)}
        >
          <Reply size={14} /> Responder
        </button>
        <button
          type="button"
          onClick={() => handleReplyAction("replyAll")}
          title="Responder a todos"
          style={toolbarButtonStyle(false)}
        >
          <ReplyAll size={14} /> Responder a todos
        </button>
        <button
          type="button"
          onClick={() => handleReplyAction("forward")}
          title="Reenviar"
          style={toolbarButtonStyle(false)}
        >
          <Forward size={14} /> Reenviar
        </button>

        <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)", margin: "0 2px" }} />

        <button
          type="button"
          onClick={handleArchive}
          disabled={isActing}
          title="Archivar"
          style={toolbarButtonStyle(isActing)}
        >
          <Archive size={14} /> Archivar
        </button>

        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setIsMoveOpen((open) => !open)}
            disabled={isActing || !moveTargets.length}
            title="Mover a…"
            style={toolbarButtonStyle(isActing || !moveTargets.length)}
          >
            <FolderInput size={14} /> Mover a…
          </button>
          {isMoveOpen && (
            <>
              {/* Overlay invisible para cerrar el menú al clickear afuera. */}
              <div
                onClick={() => setIsMoveOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 1 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  minWidth: 180,
                  maxHeight: 260,
                  overflowY: "auto",
                  background: "var(--surface-1)",
                  border: "0.5px solid var(--border)",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  zIndex: 2,
                  padding: 4,
                }}
              >
                {moveTargets.map((mb) => (
                  <button
                    key={mb.id}
                    type="button"
                    onClick={() => handleMove(mb.id)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: "transparent",
                      color: "var(--text-primary)",
                      fontSize: 13,
                      padding: "6px 8px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    {mailboxLabel(mb)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={handleDelete}
          disabled={isActing}
          title={isTrash ? "Eliminar permanentemente" : "Eliminar"}
          style={toolbarButtonStyle(isActing)}
        >
          <Trash2 size={14} /> Eliminar
        </button>
        {actionError && (
          <span style={{ fontSize: 12, color: "#b91c1c", marginLeft: 4 }}>{actionError}</span>
        )}
      </div>

      <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 500 }}>{subject}</p>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "var(--color-selected-bg)",
            color: "var(--color-selected-fg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {fromName.slice(0, 2).toUpperCase()}
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>
          {fromName} {fromEmail && <>&lt;{fromEmail}&gt;</>}
        </p>
      </div>

      <div
        style={{
          borderTop: "0.5px solid var(--border)",
          paddingTop: 16,
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {html ? (
          <EmailBody html={html} theme={theme} />
        ) : text ? (
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "var(--font-body)", fontSize: 14 }}>
            {text}
          </pre>
        ) : (
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>Este correo no tiene contenido.</p>
        )}
      </div>

      {!!message.attachments?.length && (
        <div
          style={{
            flexShrink: 0,
            marginTop: 16,
            paddingTop: 16,
            borderTop: "0.5px solid var(--border)",
          }}
        >
          <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
            {message.attachments.length === 1 ? "1 adjunto" : `${message.attachments.length} adjuntos`}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            {message.attachments.map((att) => (
              <a
                key={att.blobId}
                href={attachmentDownloadUrl(att)}
                download={att.name || undefined}
                title="Descargar"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  border: "0.5px solid var(--border)",
                  borderRadius: 10,
                  background: "var(--surface-1)",
                  textDecoration: "none",
                  color: "var(--text-primary)",
                  minWidth: 0,
                }}
              >
                <span style={{ color: "var(--text-secondary)", flexShrink: 0 }}>
                  {attachmentIcon(att.type, att.name)}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {sanitizeHeaderText(att.name) || "(sin nombre)"}
                  </p>
                  <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)" }}>
                    {formatSize(att.size)}
                  </p>
                </div>
                <Download size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const OFFICE_EXTENSIONS: Record<string, string[]> = {
  spreadsheet: ["xls", "xlsx", "csv", "ods"],
  archive: ["zip", "rar", "7z", "tar", "gz"],
};

function attachmentIcon(type: string, name: string | null) {
  const mime = type.toLowerCase();
  const ext = (name?.split(".").pop() || "").toLowerCase();
  const props = { size: 26 };

  if (mime.startsWith("image/")) return <FileImage {...props} />;
  if (mime.startsWith("video/")) return <FileVideo {...props} />;
  if (mime.startsWith("audio/")) return <FileAudio {...props} />;
  if (mime.includes("zip") || mime.includes("compressed") || OFFICE_EXTENSIONS.archive.includes(ext)) {
    return <FileArchive {...props} />;
  }
  if (mime.includes("sheet") || mime.includes("excel") || OFFICE_EXTENSIONS.spreadsheet.includes(ext)) {
    return <FileSpreadsheet {...props} />;
  }
  if (mime === "application/pdf" || mime.startsWith("text/") || mime.includes("word")) {
    return <FileText {...props} />;
  }
  return <File {...props} />;
}

function attachmentDownloadUrl(att: { blobId: string; type: string; name: string | null }): string {
  const params = new URLSearchParams({
    type: att.type || "application/octet-stream",
    name: att.name || "adjunto",
  });
  return `/api/attachments/${encodeURIComponent(att.blobId)}?${params.toString()}`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAddr(a: EmailAddress): string {
  return a.name ? `${a.name} <${a.email}>` : a.email;
}

function dedupeExcluding(addrs: EmailAddress[], exclude: Set<string>): EmailAddress[] {
  const seen = new Set<string>();
  const result: EmailAddress[] = [];
  for (const a of addrs) {
    const key = a.email.toLowerCase();
    if (exclude.has(key) || seen.has(key)) continue;
    seen.add(key);
    result.push(a);
  }
  return result;
}

function withPrefix(subject: string, prefix: string): string {
  const trimmed = subject.trim();
  return new RegExp(`^${prefix}:`, "i").test(trimmed) ? trimmed : `${prefix}: ${trimmed || "(sin asunto)"}`;
}

// Arma el draft de Responder/Responder a todos/Reenviar. plainBody ya viene
// resuelto por el caller (texto plano si existía, si no html→texto vía
// htmlToPlainText) para no duplicar esa extracción acá.
function buildReplyDraft(
  message: MessageFull,
  mode: "reply" | "replyAll" | "forward",
  myEmail: string | undefined,
  plainBody: string,
): ComposeDraft {
  const subject = sanitizeHeaderText(message.subject || "");
  const date = new Date(message.receivedAt).toLocaleString();
  const fromLabel = (message.from ?? []).map(formatAddr).join(", ") || "(desconocido)";

  if (mode === "forward") {
    const toLabel = (message.to ?? []).map(formatAddr).join(", ") || "(desconocido)";
    return {
      subject: withPrefix(subject, "Fwd"),
      body: `\n\n---------- Mensaje reenviado ----------\nDe: ${fromLabel}\nFecha: ${date}\nAsunto: ${subject || "(sin asunto)"}\nPara: ${toLabel}\n\n${plainBody}`,
      attachments: message.attachments?.map((a) => ({
        blobId: a.blobId,
        type: a.type,
        name: a.name ?? "adjunto",
        size: a.size,
      })),
    };
  }

  // Preferí Reply-To si el remitente lo seteó, si no From (RFC 5322 §3.6.2).
  const replyTargets = message.replyTo?.length ? message.replyTo : (message.from ?? []);
  const selfExclude = new Set(myEmail ? [myEmail.toLowerCase()] : []);
  const toAddrs = dedupeExcluding(replyTargets, selfExclude);

  let ccAddrs: EmailAddress[] = [];
  if (mode === "replyAll") {
    const alreadyInTo = new Set([...selfExclude, ...toAddrs.map((a) => a.email.toLowerCase())]);
    ccAddrs = dedupeExcluding([...(message.to ?? []), ...(message.cc ?? [])], alreadyInTo);
  }

  const quoted = plainBody
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");

  return {
    to: toAddrs.map((a) => a.email).join(", "),
    cc: ccAddrs.length ? ccAddrs.map((a) => a.email).join(", ") : undefined,
    subject: withPrefix(subject, "Re"),
    body: `\n\nEl ${date}, ${fromLabel} escribió:\n${quoted}`,
    inReplyTo: message.messageId ?? undefined,
    references: [...(message.references ?? []), ...(message.messageId ?? [])],
  };
}

function toolbarButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "0.5px solid var(--border)",
    background: "var(--surface-1)",
    color: "var(--text-secondary)",
    borderRadius: 8,
    fontSize: 12,
    padding: "6px 12px",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };
}
